import { EditableRefKind, } from '@numistic/contracts';
import { queryOptions, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient, } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDataClient } from './provider.js';
export const queryKeys = {
    readiness: ['system', 'readiness'],
    session: ['auth', 'session'],
    deviceSessions: ['auth', 'device-sessions'],
    invite: (token) => ['invites', token],
    adminUsers: ['admin', 'users'],
    adminInvites: ['admin', 'invites'],
    adminSettings: ['admin', 'settings'],
    meta: ['meta'],
    catalog: ['catalog'],
    catalogList: (input) => ['catalog', 'list', input],
    catalogItem: (id) => ['catalog', 'item', id],
    collections: ['collections'],
    locations: ['locations'],
    specimens: ['specimens'],
    specimenList: (input) => ['specimens', 'list', input],
    specimen: (id) => ['specimens', 'item', id],
    tags: ['tags'],
    counterparties: ['counterparties'],
    savedFilters: ['saved-filters'],
    stats: (groupBy) => ['stats', groupBy],
    numistaStatus: ['numista', 'status'],
    profile: ['profile'],
    catalogHistory: (id) => ['catalog', 'history', id],
    catalogChanges: ['catalog', 'changes'],
    adminMeta: ['admin', 'meta'],
    shares: (collectionId) => ['shares', collectionId],
    publicShare: (token) => ['public-share', token],
    publicShareList: (token, input) => ['public-share', token, 'list', input],
    publicShareItem: (token, id) => ['public-share', token, 'item', id],
    statsSummary: (scope) => ['stats', 'summary', scope],
    statsBreakdown: (input) => ['stats', 'breakdown', input],
    numistaSearch: (input) => ['numista', 'search', input],
    numistaLists: (kind) => ['numista', 'lists', kind],
    numistaPreview: (id) => ['numista', 'preview', id],
    refs: (kind, includeInactive = false) => ['refs', kind, includeInactive],
};
/** Метаданные меняются только при деплое — держим их в кэше всю сессию. */
export function metaQuery(client) {
    return queryOptions({
        queryKey: queryKeys.meta,
        queryFn: () => client.meta.get(),
        staleTime: Infinity,
    });
}
export function useMeta() {
    return useQuery(metaQuery(useDataClient()));
}
export function useRefList(kind, options = {}) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.refs(kind, options.includeInactive),
        queryFn: () => client.refs.list(kind, { includeInactive: options.includeInactive }),
        staleTime: 5 * 60_000,
        enabled: options.enabled ?? true,
    });
}
/** id → русское название для отображения ссылок на справочник (включая неактивные записи). */
export function useRefNames(kind, enabled = true) {
    const list = useRefList(kind ?? 'issuers', {
        includeInactive: true,
        enabled: enabled && kind !== null,
    });
    return useMemo(() => new Map((list.data ?? []).map((item) => [item.id, item.name.ru])), [list.data]);
}
export function useSaveRef(kind) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, input, }) => id ? client.refs.update(kind, id, input) : client.refs.create(kind, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['refs', kind] }),
    });
}
/** Опции запроса сессии — нужны и хукам, и guard'ам роутера (вне React). */
export function sessionQuery(client) {
    return queryOptions({
        queryKey: queryKeys.session,
        queryFn: () => client.auth.getSession(),
        staleTime: 60_000,
    });
}
export function useReadiness() {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.readiness,
        queryFn: () => client.system.readiness(),
        refetchInterval: 60_000,
    });
}
export function useSession() {
    return useQuery(sessionQuery(useDataClient()));
}
export function useSignIn() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.auth.signIn,
        // После входа видны записи «для участников» — списки каталога, закэшированные гостем, устарели.
        onSuccess: () => Promise.all([
            qc.invalidateQueries({ queryKey: queryKeys.session }),
            qc.invalidateQueries({ queryKey: queryKeys.catalog }),
        ]),
    });
}
export function useSignOut() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.auth.signOut,
        onSuccess: () => {
            // Данные прошлого пользователя не должны пережить выход.
            qc.clear();
            qc.setQueryData(queryKeys.session, null);
        },
    });
}
export function useRequestPasswordReset() {
    return useMutation({ mutationFn: useDataClient().auth.requestPasswordReset });
}
export function useResetPassword() {
    return useMutation({ mutationFn: useDataClient().auth.resetPassword });
}
export function useDeviceSessions() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.deviceSessions, queryFn: () => client.auth.listSessions() });
}
export function useRevokeSession() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.auth.revokeSession,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.deviceSessions }),
    });
}
export function useInvite(token) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.invite(token),
        queryFn: () => client.invites.inspect(token),
    });
}
export function useAcceptInvite() {
    return useMutation({ mutationFn: useDataClient().invites.accept });
}
export function useAdminUsers() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.adminUsers, queryFn: () => client.admin.listUsers() });
}
export function useAdminInvites() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.adminInvites, queryFn: () => client.admin.listInvites() });
}
export function useCreateInvite() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.admin.createInvite(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminInvites }),
    });
}
export function useRevokeInvite() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.admin.revokeInvite,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminInvites }),
    });
}
export function useAdminSettings() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.adminSettings, queryFn: () => client.admin.getSettings() });
}
export function useUpdateAdminSettings() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.admin.updateSettings(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.adminSettings }),
    });
}
function combineNames(results) {
    const names = new Map();
    for (const r of results)
        for (const item of r.data ?? [])
            names.set(item.id, item.name.ru);
    return names;
}
/**
 * id → название по всем нужным справочникам сразу (включая неактивные записи).
 * Число запросов фиксировано, загружаются только перечисленные виды.
 */
export function useRefNameMap(kinds) {
    const client = useDataClient();
    return useQueries({
        queries: EditableRefKind.options.map((kind) => ({
            queryKey: queryKeys.refs(kind, true),
            queryFn: () => client.refs.list(kind, { includeInactive: true }),
            staleTime: 5 * 60_000,
            enabled: kinds.has(kind),
        })),
        combine: combineNames,
    });
}
// ---------- каталог ----------
export function useCatalogList(input) {
    const client = useDataClient();
    return useInfiniteQuery({
        queryKey: queryKeys.catalogList(input),
        queryFn: ({ pageParam }) => client.catalog.list({ ...input, cursor: pageParam ?? undefined }),
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor,
    });
}
export function useCatalogItem(id) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.catalogItem(id),
        queryFn: () => client.catalog.get(id),
        enabled: Boolean(id),
    });
}
/**
 * Любая правка записи каталога: обновляем карточку, её историю, журнал изменений, списки
 * и экземпляры (их названия и значения наследуются из каталога).
 */
function useCatalogMutation(fn, itemIdOf) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => fn(client, input),
        onSuccess: (out, input) => {
            const id = itemIdOf?.(input, out);
            if (id) {
                void qc.invalidateQueries({ queryKey: queryKeys.catalogItem(id) });
                void qc.invalidateQueries({ queryKey: queryKeys.catalogHistory(id) });
            }
            void qc.invalidateQueries({ queryKey: ['catalog', 'list'] });
            void qc.invalidateQueries({ queryKey: queryKeys.catalogChanges });
            void qc.invalidateQueries({ queryKey: queryKeys.specimens });
        },
    });
}
export const useCreateCatalogItem = () => useCatalogMutation((c, input) => c.catalog.create(input), (_, out) => out.id);
export const useUpdateCatalogItem = () => useCatalogMutation((c, input) => c.catalog.update(input), (i) => i.id);
export const useSetCatalogVisibility = () => useCatalogMutation((c, i) => c.catalog.setVisibility(i.id, i.visibility), (i) => i.id);
export const useRemoveCatalogItem = () => useCatalogMutation((c, id) => c.catalog.remove(id));
export const useCreateVariant = (itemId) => useCatalogMutation((c, i) => c.catalog.createVariant(itemId, i), () => itemId);
export const useUpdateVariant = (itemId) => useCatalogMutation((c, i) => c.catalog.updateVariant(i.id, i), () => itemId);
export const useRemoveVariant = (itemId) => useCatalogMutation((c, id) => c.catalog.removeVariant(id), () => itemId);
export const useAttachImage = (itemId) => useCatalogMutation((c, i) => c.catalog.attachImage(itemId, i), () => itemId);
export const useUpdateImage = (itemId) => useCatalogMutation((c, i) => c.catalog.updateImage(i.id, i), () => itemId);
export const useRemoveImage = (itemId) => useCatalogMutation((c, id) => c.catalog.removeImage(id), () => itemId);
export function useUploadMedia() {
    return useMutation({ mutationFn: useDataClient().media.upload });
}
// ---------- коллекции, места хранения, экземпляры ----------
export function useTree(kind, options = {}) {
    const client = useDataClient();
    return useQuery({
        enabled: options.enabled ?? true,
        queryKey: queryKeys[kind],
        queryFn: () => (kind === 'collections' ? client.collections.tree() : client.locations.tree()),
    });
}
/** Любая правка данных коллекции: деревья, списки экземпляров и счётчики устаревают вместе. */
function useCollectionMutation(fn) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => fn(client, input),
        onSuccess: () => {
            for (const key of [
                queryKeys.collections,
                queryKeys.locations,
                queryKeys.specimens,
                queryKeys.tags,
                queryKeys.counterparties,
                ['stats'],
                // Метки «в коллекции» и фильтр по ним в каталоге.
                ['catalog', 'list'],
            ])
                void qc.invalidateQueries({ queryKey: key });
        },
    });
}
export const useCreateNode = (kind) => useCollectionMutation(async (c, input) => kind === 'collections'
    ? c.collections.create(input)
    : c.locations.create({ kind: 'other', ...input }));
export const useUpdateNode = (kind) => useCollectionMutation(async (c, input) => {
    const { description, kind: storageKind, ...common } = input;
    return kind === 'collections'
        ? c.collections.update({ ...common, ...(description !== undefined ? { description } : {}) })
        : c.locations.update({ ...common, ...(storageKind ? { kind: storageKind } : {}) });
});
export const useRemoveNode = (kind) => useCollectionMutation((c, id) => kind === 'collections' ? c.collections.remove(id) : c.locations.remove(id));
export function useSpecimenList(input, enabled = true) {
    const client = useDataClient();
    return useInfiniteQuery({
        queryKey: queryKeys.specimenList(input),
        queryFn: ({ pageParam }) => client.specimens.list({ ...input, cursor: pageParam ?? undefined }),
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor,
        enabled,
    });
}
export function useSpecimen(id) {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.specimen(id), queryFn: () => client.specimens.get(id) });
}
export const useCreateSpecimen = () => useCollectionMutation((c, input) => c.specimens.create(input));
export const useUpdateSpecimen = () => {
    const qc = useQueryClient();
    // Свежая версия сразу в кэше: следующая правка (например, быстрый клик по тегам)
    // не ждёт перезапроса и не ловит VERSION_CONFLICT.
    return useCollectionMutation(async (c, input) => {
        const out = await c.specimens.update(input);
        qc.setQueryData(queryKeys.specimen(out.id), out);
        return out;
    });
};
export const useRemoveSpecimen = () => useCollectionMutation((c, id) => c.specimens.remove(id));
export const useDuplicateSpecimen = () => useCollectionMutation((c, id) => c.specimens.duplicate(id));
export const useBulkSpecimens = () => useCollectionMutation((c, input) => c.specimens.bulk(input));
export const useCreateEvent = (specimenId) => useCollectionMutation((c, input) => c.specimens.createEvent(specimenId, input));
export const useRemoveEvent = () => useCollectionMutation((c, id) => c.specimens.removeEvent(id));
export const useCreateValuation = (specimenId) => useCollectionMutation((c, input) => c.specimens.createValuation(specimenId, input));
export const useRemoveValuation = () => useCollectionMutation((c, id) => c.specimens.removeValuation(id));
export const useCreateNote = (specimenId) => useCollectionMutation((c, bodyMd) => c.specimens.createNote(specimenId, bodyMd));
export const useRemoveNote = () => useCollectionMutation((c, id) => c.specimens.removeNote(id));
export function useTags() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.tags, queryFn: () => client.tags.list() });
}
export const useCreateTag = () => useCollectionMutation((c, input) => c.tags.create(input));
export const useUpdateTag = () => useCollectionMutation((c, input) => c.tags.update(input));
export const useRemoveTag = () => useCollectionMutation((c, id) => c.tags.remove(id));
/** Продавцы и площадки (справочник для «Где / у кого» и вкладки в настройках). */
export function useCounterparties() {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.counterparties,
        queryFn: () => client.counterparties.list(),
        staleTime: 60_000,
    });
}
export const useCreateCounterparty = () => useCollectionMutation((c, input) => c.counterparties.create(input));
export const useUpdateCounterparty = () => useCollectionMutation((c, input) => c.counterparties.update(input));
export const useRemoveCounterparty = () => useCollectionMutation((c, id) => c.counterparties.remove(id));
export function useSavedFilters() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.savedFilters, queryFn: () => client.savedFilters.list() });
}
export function useCreateSavedFilter() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.savedFilters.create,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.savedFilters }),
    });
}
export function useRemoveSavedFilter() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: client.savedFilters.remove,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.savedFilters }),
    });
}
export function useStats(groupBy, enabled = true) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.stats(groupBy),
        queryFn: () => client.stats.counts(groupBy),
        enabled,
    });
}
/**
 * Операции с фото записи каталога или экземпляра. Правка и удаление — общие эндпоинты /images/*,
 * различается только прикрепление и какой кеш сбрасывать.
 */
/** Скопировать фото записи каталога в фото экземпляра. */
export function useCopyCatalogImages(specimenId) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => client.specimens.copyCatalogImages(specimenId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: queryKeys.specimen(specimenId) });
            void qc.invalidateQueries({ queryKey: queryKeys.specimens });
        },
    });
}
export function useImageMutations(subject) {
    const client = useDataClient();
    const qc = useQueryClient();
    const refresh = () => {
        void qc.invalidateQueries({
            queryKey: subject.kind === 'catalog'
                ? queryKeys.catalogItem(subject.id)
                : queryKeys.specimen(subject.id),
        });
        void qc.invalidateQueries({
            queryKey: subject.kind === 'catalog' ? ['catalog', 'list'] : queryKeys.specimens,
        });
    };
    return {
        attach: useMutation({
            mutationFn: (input) => subject.kind === 'catalog'
                ? client.catalog.attachImage(subject.id, input)
                : client.specimens.attachImage(subject.id, input),
            onSuccess: refresh,
        }),
        update: useMutation({
            mutationFn: (input) => client.catalog.updateImage(input.id, input),
            onSuccess: refresh,
        }),
        remove: useMutation({
            mutationFn: (id) => client.catalog.removeImage(id),
            onSuccess: refresh,
        }),
    };
}
export function useNumistaStatus(enabled = true) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.numistaStatus,
        queryFn: () => client.numista.status(),
        enabled,
    });
}
/**
 * Поиск в Numista тратит квоту: запрос уходит только по явной команде — когда в адресе есть
 * текст или фильтр, без которого API не ищет (эмитент, каталог, год).
 */
export function useNumistaSearch(input) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useQuery({
        queryKey: queryKeys.numistaSearch(input ?? {}),
        // Запрос тратит квоту — счётчик на странице обновляется следом.
        queryFn: () => client.numista
            .search(input)
            .finally(() => void qc.invalidateQueries({ queryKey: queryKeys.numistaStatus })),
        enabled: Boolean(input),
        staleTime: 60 * 60 * 1000,
        retry: false,
    });
}
/** Эмитенты и каталоги Numista для фильтров — грузятся, только когда фильтры открыты. */
export function useNumistaList(kind, enabled) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.numistaLists(kind),
        queryFn: () => (kind === 'issuers' ? client.numista.issuers() : client.numista.catalogues()),
        enabled,
        staleTime: Infinity,
        retry: false,
    });
}
export function useNumistaPreview(numistaId) {
    const client = useDataClient();
    const qc = useQueryClient();
    return useQuery({
        queryKey: queryKeys.numistaPreview(numistaId ?? 0),
        queryFn: () => client.numista
            .preview(numistaId)
            .finally(() => void qc.invalidateQueries({ queryKey: queryKeys.numistaStatus })),
        enabled: Boolean(numistaId),
        staleTime: 60 * 60 * 1000,
        retry: false,
    });
}
export function useNumistaImport() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.numista.import(input),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: queryKeys.catalog }),
                qc.invalidateQueries({ queryKey: queryKeys.specimens }),
                qc.invalidateQueries({ queryKey: ['numista'] }),
                // Импорт мог добавить единицы, периоды, типографии и системы нумерации.
                qc.invalidateQueries({ queryKey: ['refs'] }),
            ]);
        },
    });
}
export function useStatsSummary(scope = {}) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.statsSummary(scope),
        queryFn: () => client.stats.summary(scope),
    });
}
export function useStatsBreakdown(groupBy, scope = {}) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.statsBreakdown({ ...scope, groupBy }),
        queryFn: () => client.stats.breakdown({ ...scope, groupBy }),
    });
}
export function useProfile() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.profile, queryFn: () => client.profile.get() });
}
export function useUpdateProfile() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.profile.update(input),
        onSuccess: (profile) => {
            qc.setQueryData(queryKeys.profile, profile);
            // Суммы в базовой валюте пересчитаны на сервере.
            for (const key of [
                queryKeys.specimens,
                ['stats'],
                queryKeys.counterparties,
                queryKeys.session,
            ])
                void qc.invalidateQueries({ queryKey: key });
        },
    });
}
export function useShares(collectionId) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.shares(collectionId ?? ''),
        queryFn: () => client.shares.list(collectionId),
        enabled: Boolean(collectionId),
    });
}
export function useCreateShare() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.shares.create(input),
        onSuccess: (_r, input) => qc.invalidateQueries({ queryKey: queryKeys.shares(input.collectionId) }),
    });
}
export function useRevokeShare() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => client.shares.revoke(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['shares'] }),
    });
}
export function usePublicShare(token) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.publicShare(token),
        queryFn: () => client.publicShare.get(token),
        retry: false,
    });
}
export function usePublicShareSpecimens(token, input) {
    const client = useDataClient();
    return useInfiniteQuery({
        queryKey: queryKeys.publicShareList(token, input),
        queryFn: ({ pageParam }) => client.publicShare.specimens({ token, ...input, cursor: pageParam ?? undefined }),
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor,
        retry: false,
    });
}
export function usePublicShareSpecimen(token, id) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.publicShareItem(token, id),
        queryFn: () => client.publicShare.specimen(token, id),
        retry: false,
    });
}
export function useCsvColumns() {
    const client = useDataClient();
    return useMutation({ mutationFn: (file) => client.io.csvColumns(file) });
}
/** Импорт: dry-run ничего не меняет; после настоящего импорта обновляются коллекции и статистика. */
export function useImport() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => input.mapping
            ? client.io.importCsv(input.file, input.mapping, input.dryRun)
            : client.io.importJson(input.file, input.dryRun),
        onSuccess: (_r, input) => {
            if (input.dryRun)
                return;
            for (const key of [
                queryKeys.collections,
                queryKeys.locations,
                queryKeys.specimens,
                queryKeys.tags,
                queryKeys.catalog,
                ['stats'],
            ])
                void qc.invalidateQueries({ queryKey: key });
        },
    });
}
export function useAdminMeta() {
    const client = useDataClient();
    return useQuery({ queryKey: queryKeys.adminMeta, queryFn: () => client.adminMeta.get() });
}
/** Любая операция админки метаданных; после неё обновляются и обзор, и /meta (формы). */
export function useAdminMetaMutation() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ group, op, input }) => client.adminMeta.mutate(group, op, input),
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: queryKeys.adminMeta }),
                qc.invalidateQueries({ queryKey: queryKeys.meta }),
            ]);
        },
    });
}
export function useCatalogHistory(id, enabled = true) {
    const client = useDataClient();
    return useQuery({
        queryKey: queryKeys.catalogHistory(id),
        queryFn: () => client.catalog.history(id),
        enabled,
    });
}
export function useCatalogChanges() {
    const client = useDataClient();
    return useInfiniteQuery({
        queryKey: queryKeys.catalogChanges,
        queryFn: ({ pageParam }) => client.catalog.changes({ cursor: pageParam ?? undefined, limit: 50 }),
        initialPageParam: null,
        getNextPageParam: (last) => last.nextCursor,
    });
}
export function useBulkCatalog() {
    const client = useDataClient();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.catalog.bulk(input),
        // Экземпляры наследуют значения записей каталога — их списки тоже устарели.
        onSuccess: () => Promise.all([
            qc.invalidateQueries({ queryKey: queryKeys.catalog }),
            qc.invalidateQueries({ queryKey: queryKeys.specimens }),
        ]),
    });
}
//# sourceMappingURL=hooks.js.map